import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Client, ClientLifecycleStage, LIFECYCLE_STAGES, DEAL_TYPES, PRIORITY_TIERS } from '@/types/crm';
import { formatDistanceToNow } from 'date-fns';
import { Star, Ghost } from 'lucide-react';

const STAGE_SORT_ORDER: Record<ClientLifecycleStage, number> = {
  lead: 0,
  opportunity: 1,
  interest: 2,
  deck_sent: 3,
  closing: 4,
  committed: 5,
  active: 6,
  churned: 7,
  paused: 8,
};

interface ClientPipelineTableProps {
  clients: Client[];
  onClientClick?: (client: Client) => void;
  selectedClientIds?: Set<string>;
  onToggleSelect?: (clientId: string) => void;
  onToggleSelectAll?: () => void;
}

export function ClientPipelineTable({
  clients,
  onClientClick,
  selectedClientIds = new Set(),
  onToggleSelect,
  onToggleSelectAll,
}: ClientPipelineTableProps) {
  const sortedClients = [...clients].sort((a, b) => {
    const stageDiff = STAGE_SORT_ORDER[a.lifecycle_stage] - STAGE_SORT_ORDER[b.lifecycle_stage];
    if (stageDiff !== 0) return stageDiff;
    return a.company_name.localeCompare(b.company_name);
  });

  const allSelected = clients.length > 0 && selectedClientIds.size === clients.length;

  const formatCurrency = (amount?: number | null) => {
    if (!amount || amount <= 0) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-10">
            <Checkbox
              checked={allSelected}
              onCheckedChange={() => onToggleSelectAll?.()}
            />
          </TableHead>
          <TableHead>Company</TableHead>
          <TableHead>Stage</TableHead>
          <TableHead>Contact</TableHead>
          <TableHead>Deal Type</TableHead>
          <TableHead className="text-right">Value</TableHead>
          <TableHead>Tier</TableHead>
          <TableHead>Sales Owner</TableHead>
          <TableHead>Last Touch</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedClients.map((client) => {
          const isSelected = selectedClientIds.has(client.id);
          const stageConfig = LIFECYCLE_STAGES[client.lifecycle_stage];
          const displayValue = client.confirmed_monthly_revenue || client.estimated_monthly_value;

          return (
            <TableRow
              key={client.id}
              data-state={isSelected ? 'selected' : undefined}
              className="cursor-pointer"
              onClick={() => onToggleSelect ? onToggleSelect(client.id) : onClientClick?.(client)}
            >
              <TableCell>
                <Checkbox
                  checked={isSelected}
                  onClick={(e) => e.stopPropagation()}
                  onCheckedChange={() => onToggleSelect?.(client.id)}
                />
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2 font-medium">
                  {client.company_name}
                  {client.is_ghosty && <Ghost className="h-3.5 w-3.5 text-muted-foreground" />}
                </div>
              </TableCell>
              <TableCell>
                <Badge variant="outline" className="text-xs">
                  {stageConfig.label}
                </Badge>
              </TableCell>
              <TableCell className="text-muted-foreground">
                {client.primary_contact_name}
              </TableCell>
              <TableCell>
                {client.deal_type && (
                  <Badge variant="secondary" className="text-xs">
                    {DEAL_TYPES[client.deal_type].label}
                  </Badge>
                )}
              </TableCell>
              <TableCell className="text-right font-serif">
                {formatCurrency(displayValue)}
                {client.revenue_type === 'one_off' && (
                  <span className="ml-1 text-xs text-muted-foreground">one-time</span>
                )}
              </TableCell>
              <TableCell>
                {client.priority_tier && PRIORITY_TIERS[client.priority_tier] && (
                  <Badge
                    variant={PRIORITY_TIERS[client.priority_tier].color as 'destructive' | 'warning' | 'secondary'}
                    className="text-xs"
                  >
                    <Star className="h-3 w-3 mr-0.5" />
                    T{client.priority_tier}
                  </Badge>
                )}
              </TableCell>
              <TableCell className="text-muted-foreground">
                {client.assigned_sales_owner?.name || '-'}
              </TableCell>
              <TableCell className="text-muted-foreground text-xs">
                {client.last_activity_at
                  ? formatDistanceToNow(new Date(client.last_activity_at), { addSuffix: true })
                  : '-'}
              </TableCell>
            </TableRow>
          );
        })}
        {sortedClients.length === 0 && (
          <TableRow>
            <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
              No clients found
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}
